import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentsApi } from '../../utils/api';
import {
    Wallet,
    Plus,
    Loader2,
    DollarSign,
    ArrowUpRight,
    ArrowDownLeft,
    CreditCard,
    Bitcoin,
    TrendingUp,
    Send,
    Clock,
    CheckCircle,
    AlertCircle,
    RefreshCw,
    Building
} from 'lucide-react';

export default function PaymentsModule() {
    const { projectId } = useParams();
    const queryClient = useQueryClient();
    const [showPayoutModal, setShowPayoutModal] = useState(false);
    const [payoutAmount, setPayoutAmount] = useState('');
    const [payoutMethod, setPayoutMethod] = useState('bank');

    // Fetch DadyPay balance
    const { data: balanceData, isLoading: balanceLoading } = useQuery({
        queryKey: ['dadypay-balance', projectId],
        queryFn: () => paymentsApi.getDadyPayBalance(projectId!),
        enabled: !!projectId
    });

    // Fetch crypto balance
    const { data: cryptoData, isLoading: cryptoLoading } = useQuery({
        queryKey: ['crypto-balance', projectId],
        queryFn: () => paymentsApi.getCryptoBalance(projectId!),
        enabled: !!projectId
    });

    // Fetch transactions
    const { data: transactionsData, isLoading: transactionsLoading } = useQuery({
        queryKey: ['transactions', projectId],
        queryFn: () => paymentsApi.listTransactions(projectId!),
        enabled: !!projectId
    });

    // Create payout mutation
    const payoutMutation = useMutation({
        mutationFn: (data: any) => paymentsApi.createPayout({ ...data, projectId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['dadypay-balance', projectId] });
            queryClient.invalidateQueries({ queryKey: ['transactions', projectId] });
            setShowPayoutModal(false);
            setPayoutAmount('');
        }
    });

    const balance = balanceData?.data?.balance || { available: 0, pending: 0, total: 0 };
    const crypto = cryptoData?.data || { btc: 0, eth: 0, usdt: 0 };
    const transactions = transactionsData?.data?.transactions || [];

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'completed':
                return { color: 'bg-green-500/20 text-green-400', icon: CheckCircle };
            case 'pending':
                return { color: 'bg-yellow-500/20 text-yellow-400', icon: Clock };
            case 'failed':
                return { color: 'bg-red-500/20 text-red-400', icon: AlertCircle };
            default:
                return { color: 'bg-dark-700 text-dark-400', icon: Clock };
        }
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'deposit':
            case 'payment':
                return { icon: ArrowDownLeft, color: 'text-green-400' };
            case 'payout':
            case 'withdrawal':
                return { icon: ArrowUpRight, color: 'text-red-400' };
            default:
                return { icon: DollarSign, color: 'text-dark-400' };
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-3">
                        <Wallet className="w-8 h-8 text-emerald-400" />
                        Payments
                    </h1>
                    <p className="text-dark-400 mt-1">Manage payments, payouts, and crypto monitoring</p>
                </div>
                <button
                    onClick={() => setShowPayoutModal(true)}
                    className="btn btn-primary flex items-center gap-2"
                >
                    <Send className="w-4 h-4" />
                    Request Payout
                </button>
            </div>

            {/* Balance Cards */}
            <div className="grid grid-cols-3 gap-4">
                {/* DadyPay Balance */}
                <div className="card p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                            <DollarSign className="w-5 h-5 text-green-400" />
                            DadyPay Balance
                        </h3>
                        <button
                            onClick={() => queryClient.invalidateQueries({ queryKey: ['dadypay-balance', projectId] })}
                            className="text-dark-400 hover:text-white transition-colors"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </button>
                    </div>
                    {balanceLoading ? (
                        <Loader2 className="w-6 h-6 animate-spin text-primary-400" />
                    ) : (
                        <div className="space-y-3">
                            <div>
                                <p className="text-dark-400 text-sm">Available</p>
                                <p className="text-3xl font-bold text-green-400">${balance.available?.toLocaleString()}</p>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-dark-400">Pending</span>
                                <span className="text-yellow-400">${balance.pending?.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-sm border-t border-dark-700 pt-2">
                                <span className="text-dark-400">Total</span>
                                <span>${balance.total?.toLocaleString()}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Crypto Monitoring */}
                <div className="card p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                            <Bitcoin className="w-5 h-5 text-orange-400" />
                            Crypto Assets
                        </h3>
                    </div>
                    {cryptoLoading ? (
                        <Loader2 className="w-6 h-6 animate-spin text-primary-400" />
                    ) : (
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center text-xs">₿</span>
                                    Bitcoin
                                </span>
                                <span className="font-semibold">{crypto.btc || '0.000'} BTC</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center text-xs">Ξ</span>
                                    Ethereum
                                </span>
                                <span className="font-semibold">{crypto.eth || '0.000'} ETH</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center text-xs">$</span>
                                    USDT
                                </span>
                                <span className="font-semibold">${crypto.usdt || '0.00'}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Quick Stats */}
                <div className="card p-6">
                    <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                        <TrendingUp className="w-5 h-5 text-primary-400" />
                        This Month
                    </h3>
                    <div className="space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="text-dark-400">Income</span>
                            <span className="font-semibold text-green-400">
                                +${transactions.filter((t: any) => t.type === 'payment' || t.type === 'deposit')
                                    .reduce((sum: number, t: any) => sum + (t.amount || 0), 0).toLocaleString()}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-dark-400">Payouts</span>
                            <span className="font-semibold text-red-400">
                                -${transactions.filter((t: any) => t.type === 'payout' || t.type === 'withdrawal')
                                    .reduce((sum: number, t: any) => sum + (t.amount || 0), 0).toLocaleString()}
                            </span>
                        </div>
                        <div className="flex justify-between items-center border-t border-dark-700 pt-2">
                            <span className="text-dark-400">Transactions</span>
                            <span className="font-semibold">{transactions.length}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Payout Modal */}
            {showPayoutModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-dark-900 rounded-2xl p-6 w-full max-w-md border border-dark-800">
                        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                            <Send className="w-5 h-5 text-primary-400" />
                            Request Payout
                        </h2>

                        <div className="space-y-4">
                            <div>
                                <label className="label">Amount ($)</label>
                                <input
                                    type="number"
                                    value={payoutAmount}
                                    onChange={(e) => setPayoutAmount(e.target.value)}
                                    className="input"
                                    placeholder="0.00"
                                    max={balance.available}
                                />
                                <p className="text-dark-400 text-xs mt-1">Available: ${balance.available?.toLocaleString()}</p>
                            </div>

                            <div>
                                <label className="label">Payout Method</label>
                                <select
                                    value={payoutMethod}
                                    onChange={(e) => setPayoutMethod(e.target.value)}
                                    className="input"
                                >
                                    <option value="bank">Bank Transfer</option>
                                    <option value="paypal">PayPal</option>
                                    <option value="crypto">Crypto Wallet</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button onClick={() => setShowPayoutModal(false)} className="btn btn-secondary">
                                Cancel
                            </button>
                            <button
                                onClick={() => payoutMutation.mutate({ amount: parseFloat(payoutAmount), method: payoutMethod })}
                                disabled={!payoutAmount || parseFloat(payoutAmount) > balance.available || payoutMutation.isPending}
                                className="btn btn-primary flex items-center gap-2"
                            >
                                {payoutMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Send className="w-4 h-4" />
                                )}
                                Request Payout
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Transactions */}
            <div className="card">
                <div className="p-4 border-b border-dark-800">
                    <h3 className="font-semibold">Recent Transactions</h3>
                </div>
                {transactionsLoading ? (
                    <div className="flex justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
                    </div>
                ) : transactions.length === 0 ? (
                    <div className="p-12 text-center">
                        <Wallet className="w-12 h-12 text-dark-600 mx-auto mb-4" />
                        <p className="text-dark-400">No transactions yet</p>
                    </div>
                ) : (
                    <div className="divide-y divide-dark-800">
                        {transactions.map((tx: any) => {
                            const statusInfo = getStatusBadge(tx.status);
                            const StatusIcon = statusInfo.icon;
                            const typeInfo = getTypeIcon(tx.type);
                            const TypeIcon = typeInfo.icon;

                            return (
                                <div key={tx.id} className="p-4 flex items-center justify-between hover:bg-dark-800/30">
                                    <div className="flex items-center gap-4">
                                        <div className={`p-2 rounded-lg bg-dark-800 ${typeInfo.color}`}>
                                            <TypeIcon className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <p className="font-medium capitalize">{tx.type}</p>
                                            <p className="text-dark-400 text-sm">{tx.description || tx.id.substring(0, 8)}...</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-6">
                                        <span className={`px-2 py-1 rounded-full text-xs flex items-center gap-1 ${statusInfo.color}`}>
                                            <StatusIcon className="w-3 h-3" />
                                            {tx.status}
                                        </span>
                                        <div className="text-right">
                                            <p className={`font-semibold ${tx.type === 'payout' || tx.type === 'withdrawal' ? 'text-red-400' : 'text-green-400'}`}>
                                                {tx.type === 'payout' || tx.type === 'withdrawal' ? '-' : '+'}${tx.amount?.toLocaleString()}
                                            </p>
                                            <p className="text-dark-500 text-xs">{new Date(tx.createdAt).toLocaleDateString()}</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
